import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { doc, updateDoc } from 'firebase/firestore'
import { CombinedUserData, CreateUserType, PrivateUserInfoType } from '@otw/models/user'
import { apiClient, GetAllUsersResponse } from '@/core/api/apiClient'
import { unlinkContactsFromUser } from '@/core/contacts/unlinkContactsFromUser'
import { contactsQueryKey } from 'hooks/contacts/useContacts'
import { db } from '@/config/firebase'

import { useAuth } from 'contexts/AuthContext'
import { useState, useEffect, useRef } from 'react'
import { useSnackbar } from '../common/useSnackbar'

interface CreateUserResponse {
  userId: string
  message: string
}

// Define the page param type
interface PageParam {
  page: number
  lastDocId?: string | null
}

const USERS_PER_PAGE = 100

export const useManageUsers = () => {
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false)
  const [autoLoadComplete, setAutoLoadComplete] = useState(false)
  const { showError, showSuccess } = useSnackbar()

  // Track whether we're in the process of loading
  const isLoadingRef = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Store the current retry attempt when loading fails
  const retryCountRef = useRef(0)
  const MAX_RETRIES = 5

  // Infinite query to fetch users with pagination
  const {
    data,
    isLoading: authUsersLoading,
    isError: authUsersError,
    error: authUsersErrorDetails,
    refetch: refetchUsers,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['admin', 'users', 'management', searchTerm],
    initialPageParam: { page: 1 } as PageParam,
    queryFn: async ({ pageParam }) => {
      try {
        // Check if user has admin permissions
        if (userInfo?.role !== 'admin' && userInfo?.role !== 'babyAdmin') {
          throw new Error('Unauthorized: User does not have admin permissions')
        }

        const typedPageParam = pageParam as PageParam

        // Set loading state for better UX
        setLoadingMoreUsers(true)

        const options = {
          limit: USERS_PER_PAGE,
          page: typedPageParam.page,
          lastDocId: typedPageParam.lastDocId === null ? undefined : typedPageParam.lastDocId,
          search: searchTerm || undefined,
        }

        console.log(`Fetching users (page ${typedPageParam.page}, lastDocId: ${typedPageParam.lastDocId})`)
        const result = await apiClient.portal.admin.getAuthUsers(options)

        if (!result || !result.users) {
          throw new Error('No users data returned from API')
        }

        console.log(`Received ${result.users.length} users, total: ${result.total}`)
        setLoadingMoreUsers(false)
        return result
      } catch (error) {
        setLoadingMoreUsers(false)
        console.error('Error fetching users:', error)
        showError('Error fetching users')
        throw error
      }
    },
    getNextPageParam: (lastPage: GetAllUsersResponse) => {
      if (!lastPage.hasMore) {
        return undefined
      }

      const nextParams = {
        page: (lastPage.page || 1) + 1,
        lastDocId: lastPage.lastDocId,
      } as PageParam

      return nextParams
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: Boolean(userInfo?.role === 'admin' || userInfo?.role === 'babyAdmin'),
  })

  // Combine paginated results into a single array for ag-grid
  const authUsers = data
    ? {
        users: data.pages.flatMap((page: GetAllUsersResponse) => page.users || []),
        total: data.pages[0]?.total || 0,
      }
    : { users: [], total: 0 }

  // Auto-load remaining pages until all users are fetched for ag-grid
  useEffect(() => {
    if (isLoadingRef.current || isFetchingNextPage || !data || autoLoadComplete || !hasNextPage) {
      return
    }

    const currentCount = data.pages.flatMap(p => p.users || []).length
    const totalCount = data.pages[0]?.total || 0

    if (currentCount >= totalCount || retryCountRef.current >= MAX_RETRIES) {
      setAutoLoadComplete(true)
      return
    }

    isLoadingRef.current = true

    fetchNextPage()
      .then(() => {
        isLoadingRef.current = false
        retryCountRef.current = 0
      })
      .catch(error => {
        console.error('Error loading users:', error)
        isLoadingRef.current = false
        retryCountRef.current++

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
          setAutoLoadComplete(false)
        }, 1000)
      })
  }, [data, hasNextPage, isFetchingNextPage, autoLoadComplete, fetchNextPage])

  // Reset autoload state when search term changes
  useEffect(() => {
    // Reset auto-load state
    setAutoLoadComplete(false)
    retryCountRef.current = 0

    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [searchTerm])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const createUserMutation = useMutation({
    mutationFn: async (user: CreateUserType) => {
      try {
        const result = await apiClient.portal.admin.createFirebaseUser(user)

        // The axiosClient already returns the data directly, not a Response object
        // So we need to check if the result has the expected structure
        if (result && typeof result === 'object' && 'userId' in result && 'message' in result) {
          showSuccess('User created successfully')
          return result as CreateUserResponse
        } else {
          throw new Error('Invalid response format from server')
        }
      } catch (error: any) {
        showError('Error creating user')
        // If it's an axios error, it will have response.data with the error details
        if (error.response?.data) {
          const errorData = error.response.data
          const message = errorData.error || errorData.message || errorData.details || 'Failed to create user'
          throw new Error(message)
        }
        // If it's already an Error object, re-throw it
        if (error instanceof Error) {
          throw error
        }

        // Fallback error
        throw new Error('Failed to create user')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'management'] })
    },
  })

  const createDisabledUserMutation = useMutation({
    mutationFn: async (user: CreateUserType) => {
      return await apiClient.portal.admin.createDisabledFirebaseUser(user)
    },
    onMutate: async newUser => {
      await queryClient.cancelQueries({ queryKey: ['users'] })

      // Snapshot the previous value
      const previousUsers = queryClient.getQueryData(['users'])
      queryClient.setQueryData(['users', false], (old: PrivateUserInfoType[]) => {
        const optimisticUser = {
          ...newUser,
          uid: 'temp-' + Date.now(), // Temporary ID
          disabled: true,
          metadata: {
            creationTime: new Date().toISOString(),
            lastSignInTime: null,
          },
        }
        return [...old, optimisticUser]
      })

      return { previousUsers }
    },
    onError: (err, newUser, context) => {
      queryClient.setQueryData(['users'], context?.previousUsers)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const result = await apiClient.portal.admin.deleteFirebaseUser(userId)
      try {
        await unlinkContactsFromUser(userId)
      } catch (error) {
        console.error('Failed to unlink CRM contacts from deleted user:', error)
      }
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'management'] })
      queryClient.invalidateQueries({ queryKey: contactsQueryKey })
      showSuccess('User deleted successfully')
    },
  })

  const disableUserMutation = useMutation({
    mutationFn: (userId: string) => {
      return apiClient.portal.admin.disableFirebaseUser(userId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'management'] })
      showSuccess('User disabled successfully')
    },
  })

  const enableUserMutation = useMutation({
    mutationFn: (userId: string) => {
      return apiClient.portal.admin.enableFirebaseUser(userId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'management'] })
      showSuccess('User enabled successfully')
    },
  })

  const sendPasswordResetMutation = useMutation({
    mutationFn: (email: string) => {
      return apiClient.portal.admin.sendPasswordReset(email)
    },
    onSuccess: () => {
      // No need to invalidate queries as this doesn't change user data
    },
  })

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; [key: string]: any }) => {
      if (!id) throw new Error('User ID is required')

      const { isRevert, ...updateData } = data

      // Remove any undefined or null values
      const cleanedData = Object.entries(updateData).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value
          }
          return acc
        },
        {} as Record<string, any>,
      )

      // Convert string boolean values to actual booleans
      if (typeof cleanedData.disabled === 'string') {
        cleanedData.disabled = cleanedData.disabled === 'true'
      }
      if (typeof cleanedData.emailVerified === 'string') {
        cleanedData.emailVerified = cleanedData.emailVerified === 'true'
      }
      
      const result = await apiClient.portal.admin.editFirebaseUser({
        userId: id,
        ...cleanedData,
      })

      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'management'] })
      showSuccess('User updated successfully')
    },
    onError: error => {
      console.error('Error updating user:', error)
    },
  })

  const resetOnboardingMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!userId) throw new Error('User ID is required')
      
      // Update the user document directly in Firestore
      const userDocRef = doc(db, 'users', userId)
      await updateDoc(userDocRef, {
        needsOnboarding: true,
        updatedAt: new Date().toISOString(),
      })

      return { userId }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'management'] })
      showSuccess('Client onboarding reset successfully')
    },
    onError: error => {
      console.error('Error resetting onboarding:', error)
      showError('Failed to reset client onboarding')
    },
  })

  return {
    authUsers,
    authUsersLoading,
    authUsersError,
    authUsersErrorDetails,
    refetchUsers,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    searchTerm,
    setSearchTerm,
    createUserMutation,
    createDisabledUserMutation,
    updateUserMutation,
    updateUser: updateUserMutation.mutate,
    deleteUserMutation,
    deleteUser: deleteUserMutation.mutate,
    disableUserMutation,
    enableUserMutation,
    sendPasswordResetMutation,
    resetOnboardingMutation,
  }
}
